import Arrow from "@/common/icons/arrow"
import Close from "@/common/icons/close"
import Image from "next/legacy/image"
import Link from "next/link"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { A11y, Navigation } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { getCategoryIcon } from "../wild-neighbors-register/form-activity/activity"
import { allowedImageTypes } from "../wild-neighbors-register/form-activity/files"
import { Icon } from "../wild-neighbors-register/icon"
import { ImagePlaceholder } from "../wild-neighbors-single/image-placeholder"
import styles from "./swiper.module.css"

export function ActivityList({ activities = [], markers, setCurrentMarker }) {
  const [currentIndex, setCurrentIndex] = useState(1)
  const [mounted, setMounted] = useState(false)
  const swiperRef = useRef(null)
  const prevSlideRef = useRef(null)
  const nextSlideRef = useRef(null)

  useEffect(() => setMounted(true), [])

  useLayoutEffect(() => {
    if (!swiperRef.current) {
      return
    }

    swiperRef.current.swiper.navigation.update()
    swiperRef.current.swiper.update()
  }, [markers])

  return (
    <div className="relative z-10 activity-list">
      <button
        aria-label="Stäng"
        onClick={() => setCurrentMarker([])}
        className={styles.close}
        type="button">
        <Close className="w-3 h-auto text-white fill-current" />
      </button>
      {mounted && (
        <Swiper
          ref={swiperRef}
          className={styles.swiper}
          modules={[Navigation, A11y]}
          slidesPerView={1}
          navigation={{
            nextEl: ".swiper-nav-next-activity",
            prevEl: ".swiper-nav-prev-activity"
          }}
          onSlideChange={(swiper) => {
            setCurrentIndex(swiper.activeIndex + 1)
            swiper.navigation.update()
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevSlideRef.current
            swiper.params.navigation.nextEl = nextSlideRef.current
          }}>
          {markers.map((marker, index) => (
            <SwiperSlide key={index}>
              <Activity
                activities={activities}
                marker={marker}
                currentIndex={currentIndex}
              />
            </SwiperSlide>
          ))}
          {markers.length > 1 && (
            <div className="flex items-center justify-between bg-summer-green-25">
              <button
                type="button"
                aria-label="Föregående"
                ref={prevSlideRef}
                className="nav-button prev swiper-nav-prev-activity green">
                <Arrow />
              </button>
              <span className="text-base">
                {currentIndex} av {markers.length}
              </span>
              <button
                type="button"
                aria-label="Nästa"
                ref={nextSlideRef}
                className="nav-button swiper-nav-activity green">
                <Arrow />
              </button>
            </div>
          )}
        </Swiper>
      )}
    </div>
  )
}

export function Activity({ activities = [], marker, currentIndex }) {
  const [mounted, setMounted] = useState(false)
  const prevSlideRef = useRef(null)
  const nextSlideRef = useRef(null)
  const swiperRef = useRef(null)

  useEffect(() => setMounted(true), [])

  useLayoutEffect(() => {
    if (!prevSlideRef.current && !nextSlideRef.current && !swiperRef.current) {
      return
    }

    swiperRef.current.swiper.navigation.update()
    swiperRef.current.swiper.update()
  }, [currentIndex])

  const completedActivities = marker.activities.filter(
    (activity) => activity.status === 1
  )

  const completedActivitiesList = completedActivities.slice(
    0,
    completedActivities.length > 5 ? 4 : completedActivities.length
  )

  const unCompletedActivities = marker.activities.filter(
    (activity) => activity.status === 0
  )

  const unCompletedActivitiesList = unCompletedActivities.slice(
    0,
    unCompletedActivities.length > 5 ? 4 : unCompletedActivities.length
  )

  const images = marker.activities.reduce((acc, activity) => {
    const imageFiles = activity.files.filter((file) => {
      return allowedImageTypes.includes(file.type)
    })
    return [...acc, ...imageFiles]
  }, [])

  return (
    <div className="relative z-20">
      {images.length > 0 ? (
        mounted ? (
          <Swiper
            ref={swiperRef}
            className={styles.swiper}
            modules={[Navigation, A11y]}
            slidesPerView={1}
            navigation={{
              nextEl: ".swiper-nav-next",
              prevEl: ".swiper-nav-prev"
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevSlideRef.current
              swiper.params.navigation.nextEl = nextSlideRef.current
            }}>
            {images.map((image, index) => {
              if (!image.url) {
                return null
              }

              return (
                <SwiperSlide key={index}>
                  <figure className="relative aspect-w-4 aspect-h-3">
                    <Image
                      src={image.url}
                      alt={image.name}
                      layout="fill"
                      objectFit="cover"
                    />
                  </figure>
                </SwiperSlide>
              )
            })}
            <button
              type="button"
              aria-label="Föregående"
              ref={prevSlideRef}
              className="nav-button prev swiper-nav-prev-activity swiper-nav-prev">
              <Arrow />
            </button>
            <button
              type="button"
              aria-label="Nästa"
              ref={nextSlideRef}
              className="nav-button swiper-nav-activity swiper-nav-next">
              <Arrow />
            </button>
          </Swiper>
        ) : null
      ) : (
        <figure className="relative aspect-w-4 aspect-h-3">
          <ImagePlaceholder className="object-cover" />
        </figure>
      )}
      <div className="p-4 text-base">
        <h4>{marker.school_name}</h4>
        <div>{marker.class_name}</div>
        {completedActivitiesList.length > 0 && (
          <>
            <span className="block mb-2 mt-4 first:mt-0 text-xs font-medium uppercase tracking-[1px]">
              Aktiviteter vi har gjort
            </span>
            <div className="flex flex-wrap gap-2">
              {completedActivitiesList.map((activity, index) => (
                <Icon
                  key={index}
                  svg={getCategoryIcon(activities, activity.activity)}
                />
              ))}
              {completedActivities.length > 5 && (
                <div className="flex items-center justify-center text-base rounded-full w-14 h-14 bg-summer-green-25 text-black-75">
                  +{completedActivities.length - 4}
                </div>
              )}
            </div>
          </>
        )}
        {unCompletedActivitiesList.length > 0 && (
          <>
            <span className="block mb-2 mt-4 first:mt-0 text-xs font-medium uppercase tracking-[1px]">
              Aktiviteter vi vill göra
            </span>
            <div className="flex flex-wrap gap-2">
              {unCompletedActivitiesList.map((activity, index) => (
                <Icon
                  key={index}
                  svg={getCategoryIcon(activities, activity.activity)}
                  outlined
                />
              ))}
              {unCompletedActivities.length > 5 && (
                <div className="flex items-center justify-center text-base rounded-full w-14 h-14 bg-summer-green-25 text-black-75">
                  +{unCompletedActivities.length - 4}
                </div>
              )}
            </div>
          </>
        )}
        <Link
          href={marker.url}
          prefetch={false}
          className="inline-block mt-4 font-bold underline">
          Läs mer om vårt arbete
        </Link>
      </div>
    </div>
  )
}
